// @ts-nocheck

import _ from 'lodash';
import v4 from 'uuid/v4';

import { flashMessageActionCreators } from '../actions';
import {
  basketAddItem,
  basketDelItem,
  basketAlterCachedMMAPIResponseInfos,
  basketRemoveCachedMMAPIResponseInfos,
  basketUpdateItemValue,
  basketRequestSwitchMode,
} from '../actions/basket';
import { myMatchMerge, myMatchSplit, receivedGetFutureOddsEntitiesUpdates } from '../actions/mymatch';
import {
  getFutureOdds,
  getOdds,
  receivedGetFutureOddsResultEntities,
  setMyMatchMode,
  unsetMyMatchMode,
} from '../actions/mymatch';
import networkActions from '../actions/network';
import { JsonRpcApiMethodNames } from '../constants/api';
import { NoBasketSplitByDefaultMyMatchErrorIds } from '../constants/mymatch';
import { type CustomRouterHistory } from '../createHistory';
import { __ } from '../i18n/l10n';
import { selectMatchViewMode, makeNbBasketMatchSelectionsSelector } from '../selectors/match.selector';
import { makeMatchIsMyMatchAvailableSelector } from '../selectors/mymatch.selector';
import { CommonActionTypes } from '../types/actions/common';
import { MyMatchActionTypes } from '../types/actions/mymatch';
import { BasketItemStatuses, BasketModes, BasketStatuses } from '../types/basket';
import { type BetBuilderSelectionStatus } from '../types/betbuilder';
import { FlashMessageScopes, FlashMessageSeverities, FlashMessageTypes } from '../types/flashmessage';
import { MatchViewModes } from '../types/matchView';
import { type StoreType, type GenericAction } from '../types/redux';
import { RoutePatterns } from '../types/routes';

const handledActionTypes = [
  MyMatchActionTypes.GET_ODDS,
  MyMatchActionTypes.GET_FUTURE_ODDS,
  MyMatchActionTypes.REQUEST_ADD_WITH_MERGE,
  MyMatchActionTypes.REQUEST_DELETION_WITH_MERGE,
  MyMatchActionTypes.REQUEST_OUTCOME_INJECTION,
  MyMatchActionTypes.REQUEST_OUTCOME_DELETION,
  MyMatchActionTypes.REQUEST_MERGE,
  MyMatchActionTypes.REQUEST_SPLIT,
  CommonActionTypes.RECEIVE_ENTITIES,
  MyMatchActionTypes.REQUEST_TOGGLE_MY_MATCH_MODE,
  MyMatchActionTypes.SET_MY_MATCH_MODE,
  MyMatchActionTypes.INJECT_TOP_MY_MATCH_INTO_BASKET,
  MyMatchActionTypes.REMOVE_MULTICHOICE_BET_SELECTION,
  MyMatchActionTypes.SPLIT,
];

const getMyMatchSelectionItemFormat = ({
  matchId,
  betId,
  outcomeId,
  oddId,
  outcomeIds,
  status,
  error,
  isFromMultichoice,
}: {
  matchId?: number;
  betId?: number;
  outcomeId?: number;
  oddId?: number;
  outcomeIds?: Array<number>;
  status?: BetBuilderSelectionStatus;
  error?: unknown;
  isFromMultichoice?: boolean;
}) => {
  const res = {};

  if (typeof matchId === 'number') {
    res.matchId = matchId;
  }

  if (typeof betId === 'number') {
    res.betId = betId;
  }

  if (Array.isArray(outcomeIds)) {
    res.outcomeIds = outcomeIds;
  }

  if (typeof outcomeId === 'number' || typeof oddId === 'number') {
    res.outcomeId = outcomeId ?? oddId;
  }

  res.status = status ?? BasketItemStatuses.OK;

  res.error = error ?? null;

  if (typeof isFromMultichoice === 'boolean') {
    res.isFromMultichoice = isFromMultichoice;
  }

  return res;
};

const getGetOddsOutcomesFromBasketItems = (items) => [
  ...items.map(({ betId, oddId, outcomeId, outcomeIds, isFromMultichoice }) => {
    const nextItem = { betId };

    if (outcomeIds) {
      nextItem.outcomeIds = outcomeIds;
    } else if (isFromMultichoice) {
      nextItem.outcomeIds = [outcomeId];
    } else {
      nextItem.outcomeId = outcomeId ?? oddId;
    }

    return nextItem;
  }),
];

const myMatchMiddleware =
  (history: CustomRouterHistory) =>
  ({ getState, dispatch }: StoreType) =>
  (next: (action: GenericAction) => unknown) =>
  async (action: GenericAction) => {
    const { type: actionType, payload: actionPayload, hooks: actionHooks = {} } = action;

    if (handledActionTypes.indexOf(actionType) !== -1) {
      const state = getState();
      const { basket: basketState, mymatch: myMatchState } = state;
      const basketItems = basketState.items;
      const basketItemIds = Object.keys(basketItems);
      const basketItemsAsArray: Array<unknown> = basketItemIds.map((itemId) => basketItems[itemId]);

      if (actionType === MyMatchActionTypes.GET_ODDS) {
        const { matchId, outcomes } = actionPayload ?? {};

        let builtOutcomes = [];

        if (!outcomes || !Array.isArray(outcomes)) {
          for (const basketItem of basketItemsAsArray) {
            const {
              matchId: basketItemMatchId,
              betId: basketItemBetId,
              oddId: basketItemOutcomeId,
              betbuilder: basketItemBetbuilder,
              selectionItems: basketItemSelectionItems = [],
              isFromMultichoice: basketItemIsFromMultichoice,
            } = basketItem;

            if (basketItemMatchId === matchId) {
              if (basketItemBetbuilder && basketItemSelectionItems.length) {
                for (const selectionItem of basketItemSelectionItems) {
                  const { outcomeId, betId, outcomeIds, isFromMultichoice } = selectionItem;

                  if (typeof betId === 'number') {
                    if (isFromMultichoice && !Array.isArray(outcomeIds) && typeof outcomeId === 'number') {
                      builtOutcomes.push({ betId, outcomeIds: [outcomeId] });
                    } else if (Array.isArray(outcomeIds)) {
                      builtOutcomes.push({ betId, outcomeIds });
                    } else if (typeof outcomeId === 'number') {
                      builtOutcomes.push({ betId, outcomeId });
                    }
                  }
                }
              } else if (basketItemIsFromMultichoice) {
                builtOutcomes.push({ betId: basketItemBetId, outcomeIds: [basketItemOutcomeId] });
              } else {
                builtOutcomes.push({ betId: basketItemBetId, outcomeId: basketItemOutcomeId });
              }

              break;
            }
          }
        } else {
          builtOutcomes = outcomes;
        }

        if (builtOutcomes.length) {
          const queryId = v4();

          const { onSuccess } = actionHooks;

          const enhancedHooks = {
            onSuccess: (result) => {
              dispatch(basketAlterCachedMMAPIResponseInfos({ result, outcomes: builtOutcomes, matchId }));

              if (onSuccess) {
                onSuccess(result);
              }
            },
            onError: () => {
              const currentState = getState();
              const matchViewMode = selectMatchViewMode(currentState, { matchId });

              if (matchViewMode === MatchViewModes.MY_MATCH) {
                dispatch(
                  flashMessageActionCreators.addMessage({
                    scope: FlashMessageScopes.BASKET,
                    severity: FlashMessageSeverities.ERROR,
                    type: FlashMessageTypes.DISMISS_AUTO,
                    message: __('MyMatch currently unavailable'),
                  }),
                );
                dispatch(unsetMyMatchMode({ matchId }));
              }
            },
          };

          dispatch(
            networkActions.send(
              {
                myMatch: {
                  id: queryId,
                  method: JsonRpcApiMethodNames.GET_ODDS,
                  params: {
                    matchId,
                    outcomes: builtOutcomes,
                  },
                },
              },
              { queryId, hooks: enhancedHooks },
            ),
          );
        }
      }

      if (actionType === MyMatchActionTypes.GET_FUTURE_ODDS) {
        const {
          matchId: actionPayloadMatchId,
          triggerLoading: actionPayloadTriggerLoading,
          preventCallOutsideMatchRoute: actionPayloadPreventCallOutsideMatchRoute,
          shouldHideOddUpdateIndicators: actionPayloadShouldHideOddUpdateIndicators,
          forceWithLease: actionPayloadForceWithLease,
        } = actionPayload ?? {};

        const job = () => {
          const nexted = next(action);

          const builtOutcomes = [];

          const nextedState = getState();
          const { entities: { matches } = {}, basket: { status: basketStatus } = {} } = nextedState ?? {};
          const targettedMatch = matches[actionPayloadMatchId];

          if (targettedMatch?.isOutright) {
            return nexted;
          }

          if (basketStatus !== BasketStatuses.VALIDATED) {
            for (const basketItem of basketItemsAsArray) {
              const {
                matchId: basketItemMatchId,
                betId: basketItemBetId,
                oddId: basketItemOutcomeId,
                betbuilder: basketItemBetbuilder,
                selectionItems: basketItemSelectionItems = [],
                isFromMultichoice: basketItemIsFromMultichoice,
              } = basketItem;

              if (basketItemMatchId === actionPayloadMatchId) {
                if (basketItemBetbuilder && basketItemSelectionItems.length) {
                  for (const selectionItem of basketItemSelectionItems) {
                    const {
                      outcomeId: selectionItemOutcomeId,
                      betId: selectionItemBetId,
                      outcomeIds: selectionItemOutcomeIds,
                      isFromMultichoice: selectionItemIsFromMultichoice,
                    } = selectionItem;

                    if (typeof selectionItemBetId === 'number') {
                      if (
                        selectionItemIsFromMultichoice &&
                        !Array.isArray(selectionItemOutcomeIds) &&
                        typeof selectionItemOutcomeId === 'number'
                      ) {
                        builtOutcomes.push({ betId: selectionItemBetId, outcomeIds: [selectionItemOutcomeId] });
                      } else if (Array.isArray(selectionItemOutcomeIds)) {
                        builtOutcomes.push({ betId: selectionItemBetId, outcomeIds: selectionItemOutcomeIds });
                      } else if (typeof selectionItemOutcomeId === 'number') {
                        builtOutcomes.push({ betId: selectionItemBetId, outcomeId: selectionItemOutcomeId });
                      }
                    }
                  }
                } else if (basketItemIsFromMultichoice) {
                  builtOutcomes.push({ betId: basketItemBetId, outcomeIds: [basketItemOutcomeId] });
                } else {
                  builtOutcomes.push({ betId: basketItemBetId, outcomeId: basketItemOutcomeId });
                }
              }
            }
          }

          const queryId = v4();

          dispatch(
            networkActions.send(
              {
                myMatch: {
                  id: queryId,
                  method: JsonRpcApiMethodNames.GET_FUTURE_ODDS,
                  params: {
                    matchId: actionPayloadMatchId,
                    outcomes: builtOutcomes,
                  },
                },
              },
              {
                queryId,
                hooks: {
                  onSuccess: (result) => {
                    const { mymatchError } = result ?? {};
                    const { errorId } = mymatchError ?? {};

                    if (errorId) {
                      dispatch(
                        basketAlterCachedMMAPIResponseInfos({
                          result: { mymatchError },
                          outcomes: builtOutcomes,
                          matchId: actionPayloadMatchId,
                        }),
                      );
                    }

                    const matchViewMode = selectMatchViewMode(getState(), { matchId: actionPayloadMatchId });

                    if (actionPayloadShouldHideOddUpdateIndicators && matchViewMode === MatchViewModes.MY_MATCH) {
                      const documentElement = document.documentElement;

                      if (documentElement) {
                        documentElement.style.setProperty('--OddButton-showOddUpdateIndicator', '0');

                        setTimeout(() => {
                          documentElement.style.setProperty('--OddButton-showOddUpdateIndicator', '1');
                        }, 100);
                      }
                    }

                    dispatch(receivedGetFutureOddsResultEntities(result, actionPayloadMatchId));
                  },
                  onError: () => {
                    const currentState = getState();
                    const matchViewMode = selectMatchViewMode(currentState, { matchId: actionPayloadMatchId });

                    if (matchViewMode === MatchViewModes.MY_MATCH) {
                      dispatch(
                        flashMessageActionCreators.addMessage({
                          scope: FlashMessageScopes.BASKET,
                          severity: FlashMessageSeverities.ERROR,
                          type: FlashMessageTypes.DISMISS_AUTO,
                          message: __('MyMatch currently unavailable'),
                        }),
                      );
                      dispatch(unsetMyMatchMode({ matchId: actionPayloadMatchId }));
                    }
                  },
                },
                triggerLoading: !!actionPayloadTriggerLoading,
              },
            ),
          );
        };

        if (actionPayloadPreventCallOutsideMatchRoute) {
          const match = history?.location?.pathname.match(
            new RegExp(RoutePatterns.MATCH.replace(':matchId', '(\\d+)(.*)$')),
          );

          if (match) {
            const [, matchIdasStr] = match;
            const matchId = parseInt(matchIdasStr, 10);

            if (matchId === actionPayloadMatchId) {
              const isMyMatchAvailable = makeMatchIsMyMatchAvailableSelector()(state, { matchId });
              const matchViewMode = selectMatchViewMode(state, { matchId });
              const shouldGet =
                isMyMatchAvailable && (matchViewMode === MatchViewModes.MY_MATCH || actionPayloadForceWithLease);

              if (shouldGet) {
                return job();
              }
            }
          }
        } else {
          return job();
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_ADD_WITH_MERGE) {
        // Appel à la method 'GetOdds' (avec la future selection du futur item MyMatch (résultat de la fusion)
        // et les hooks = { onSuccess, onError }) pour vérifier l'éligibilité de la fusion avant de la faire

        const {
          oddId: actionPayloadOddId,
          matchId: actionPayloadMatchId,
          betId: actionPayloadBetId,
          sourceElementUuid: actionPayloadSourceElementUuid,
          isFromLineUp: actionPayloadIsFromLineUp,
          isFromMultichoice: actionPayloadIsFromMultichoice,
        } = actionPayload ?? {};

        const prevClassicItemsWithSameMatchId = basketItemsAsArray.filter(
          (item) => item && item.matchId === actionPayloadMatchId && !item.betbuilder,
        );

        let nextMyMatchSelectionWithSameMatchId = [];

        const getClassicInjection = () => [
          ...prevClassicItemsWithSameMatchId.map(
            ({
              oddId: itemWithSameMatchIdOddId,
              outcomeId: itemWidthSameMatchIdOutcomeId,
              betId: itemWithSameMatchIdBetId,
              isFromMultichoice: itemWithSameMatchIdIsFromMultichoice,
            }) => ({
              betId: itemWithSameMatchIdBetId,
              outcomeId: itemWithSameMatchIdOddId ?? itemWidthSameMatchIdOutcomeId,
              isFromMultichoice: itemWithSameMatchIdIsFromMultichoice,
            }),
          ),
          {
            betId: actionPayloadBetId,
            outcomeId: actionPayloadOddId,
            isFromMultichoice: actionPayloadIsFromMultichoice,
          },
        ];

        if (!actionPayloadIsFromMultichoice) {
          nextMyMatchSelectionWithSameMatchId = getClassicInjection();
        } else {
          const multichoiceablePrevItems = prevClassicItemsWithSameMatchId.filter(
            (item) => item.betId === actionPayloadBetId && item.isFromMultichoice,
          );

          if (multichoiceablePrevItems.length) {
            const regularPrevItems = prevClassicItemsWithSameMatchId.filter(
              (item) => !(item.betId === actionPayloadBetId && item.isFromMultichoice),
            );

            nextMyMatchSelectionWithSameMatchId = [
              ...regularPrevItems,
              {
                betId: actionPayloadBetId,
                outcomeIds: [
                  ...multichoiceablePrevItems.map(
                    ({ oddId: itemWithSameMatchIdOddId, outcomeId: itemWithSameMatchIdOutcomeId }) =>
                      itemWithSameMatchIdOddId ?? itemWithSameMatchIdOutcomeId,
                  ),
                  actionPayloadOddId,
                ],
              },
            ];
          } else {
            nextMyMatchSelectionWithSameMatchId = getClassicInjection();
          }
        }

        if (prevClassicItemsWithSameMatchId.length) {
          dispatch(
            getOdds(
              {
                matchId: actionPayloadMatchId,
                outcomes: getGetOddsOutcomesFromBasketItems(nextMyMatchSelectionWithSameMatchId),
              },
              {
                onSuccess: (result) => {
                  const { mymatchOdds: value, mymatchError } = result ?? {};
                  const { errorId } = mymatchError ?? {};

                  if (!errorId || !!~NoBasketSplitByDefaultMyMatchErrorIds.indexOf(errorId)) {
                    // La fusion est faisable
                    dispatch(
                      myMatchMerge({
                        matchId: actionPayloadMatchId,
                        itemIdsToRemove: prevClassicItemsWithSameMatchId.map((k) => k.oddId ?? k.outcomeId),
                        selectionItems: nextMyMatchSelectionWithSameMatchId.map((item) =>
                          getMyMatchSelectionItemFormat({
                            matchId: actionPayloadMatchId,
                            ...item,
                          }),
                        ),
                        value,
                        sourceElementUuid: actionPayloadSourceElementUuid,
                        oddId: actionPayloadOddId,
                      }),
                    );
                  } else {
                    // On ne peut pas fusionner la selection en un item MyMatch (selection contradictoire par ex),
                    // on ajoute donc normalement l’item au panier

                    dispatch(basketRequestSwitchMode({ mode: BasketModes.SIMPLE }));

                    dispatch(
                      basketAddItem({
                        oddId: actionPayloadOddId,
                        matchId: actionPayloadMatchId,
                        betId: actionPayloadBetId,
                        sourceElementUuid: actionPayloadSourceElementUuid,
                        isFromLineUp: actionPayloadIsFromLineUp,
                        isFromMultichoice: actionPayloadIsFromMultichoice,
                      }),
                    );
                  }
                },
              },
            ),
          );
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_DELETION_WITH_MERGE) {
        // Dans le cas ou plusieurs selections pour un même match existaient (car potentiellement contradictoires)
        // et qu'on demande la suppression d'une cote de ce match pouvant potentiellement ôter la contradiction
        // et requierant donc une fusion en un nouvel item MyMatch

        const { oddId: actionPayloadOddId, matchId: actionPayloadMatchId } = actionPayload ?? {};

        const prevClassicItemsWithSameMatchId = basketItemsAsArray.filter(
          (item) => item && item.matchId === actionPayloadMatchId && !item.betbuilder,
        );

        const restClassicItemsWithSameMatchId = prevClassicItemsWithSameMatchId.reduce((acc, _item) => {
          const {
            oddId: itemOddId,
            oddsId: itemOddsId,
            outcomeId: itemOutcomeId,
            betId: itemBetId,
            isFromMultichoice: itemIsFromMultichoice,
          } = _item;
          const computedItemOutcomeId = itemOddId ?? itemOddsId ?? itemOutcomeId;

          if (itemOddId !== actionPayloadOddId) {
            if (itemIsFromMultichoice) {
              const foundTarget = acc.find(
                (__item) =>
                  __item.betId === itemBetId && (Array.isArray(__item.outcomeIds) || __item.isFromMultichoice),
              );

              if (foundTarget) {
                if (Array.isArray(foundTarget.outcomeIds)) {
                  foundTarget.outcomeIds.push(computedItemOutcomeId);
                } else if (typeof foundTarget.outcomeId === 'number') {
                  foundTarget.outcomeIds = [foundTarget.outcomeId, computedItemOutcomeId];
                  delete foundTarget.outcomeId;
                  delete foundTarget.isFromMultichoice;
                }

                return acc;
              } else {
                const nextItem = getMyMatchSelectionItemFormat(_item);

                return [...acc, nextItem];
              }
            }

            return [...acc, getMyMatchSelectionItemFormat(_item)];
          }

          return acc;
        }, []);

        if (Array.isArray(restClassicItemsWithSameMatchId)) {
          dispatch(
            getOdds(
              {
                matchId: actionPayloadMatchId,
                outcomes: getGetOddsOutcomesFromBasketItems(restClassicItemsWithSameMatchId),
              },
              {
                onSuccess: (result) => {
                  const { mymatchOdds: value, mymatchError } = result ?? {};
                  const { errorId } = mymatchError ?? {};

                  if (!errorId || !!~NoBasketSplitByDefaultMyMatchErrorIds.indexOf(errorId)) {
                    // La fusion est faisable

                    dispatch(
                      myMatchMerge({
                        itemIdsToRemove: prevClassicItemsWithSameMatchId.map((k) => k.oddId ?? k.outcomeId),
                        matchId: actionPayloadMatchId,
                        selectionItems: restClassicItemsWithSameMatchId,
                        value,
                      }),
                    );
                  } else {
                    // On ne peut pas fusionner la selection en un item MyMatch (selection contradictoire par ex),
                    // on supprime donc normalement l’item du panier

                    dispatch(basketDelItem({ oddId: actionPayloadOddId, matchId: actionPayloadMatchId }));
                  }
                },
              },
            ),
          );
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_OUTCOME_INJECTION) {
        // Appel à la method 'GetOdds' avec la future selection du futur item MyMatch
        // (toutes les selections de l’item MyMatch existant + la nouvelle selection)
        // et les hooks = { onSuccess, onError }

        const {
          oddId: actionPayloadOddId,
          matchId: actionPayloadMatchId,
          betId: actionPayloadBetId,
          sourceElementUuid: actionPayloadSourceElementUuid,
          isFromMultichoice: actionPayloadIsFromMultichoice,
        } = actionPayload ?? {};

        const existingMyMatchItemWithSameMatchId = basketItemsAsArray.find(
          (item) => item.matchId === actionPayloadMatchId && item.betbuilder,
        );

        if (existingMyMatchItemWithSameMatchId) {
          const { selectionItems: existingMyMatchItemSelectionItems, oddId: existingMyMatchItemOddId } =
            existingMyMatchItemWithSameMatchId;

          const computeGetOddsOutcomes = () => {
            const tryToInvertIsFromMultichoiceProp = () => {
              const sameBetOutcomeIndex = existingMyMatchItemSelectionItems.findIndex(
                ({ betId, oddId, outcomeId, outcomeIds }) =>
                  betId === actionPayloadBetId &&
                  (oddId ?? outcomeId) === actionPayloadOddId &&
                  !Array.isArray(outcomeIds),
              );

              if (~sameBetOutcomeIndex) {
                // Si on a cliqué sur un outcome qui existe déjà en version classique (non inclus dans un multichance)
                // dans la selection mymatch, on réatribue sa propriété isFromMultichoice en fonction du contexte
                // du bet de l'outcome qui a été cliqué

                const sameBet = existingMyMatchItemSelectionItems[sameBetOutcomeIndex];

                if (sameBet) {
                  sameBet.isFromMultichoice = actionPayloadIsFromMultichoice;

                  return [
                    ...existingMyMatchItemSelectionItems.map(
                      ({ betId, oddId, outcomeId, outcomeIds, isFromMultichoice }) => ({
                        betId,
                        outcomeId: outcomeId ?? oddId,
                        outcomeIds,
                        isFromMultichoice,
                      }),
                    ),
                  ];
                }
              }
            };

            if (actionPayloadIsFromMultichoice) {
              // Si on a cliqué sur un outcome d'un bet multichance artificiel

              const sameMultichoiceBetIdIndex = existingMyMatchItemSelectionItems.findIndex(
                ({ betId, isFromMultichoice, outcomeIds }) =>
                  betId === actionPayloadBetId && (isFromMultichoice || Array.isArray(outcomeIds)),
              );

              if (~sameMultichoiceBetIdIndex) {
                // Si un bet multichance du même betId que l'outcome selectionné existe déjà dans le mymatch du match
                // On fait une union des anciens outcomeIds et du nouvel outcome selectionné (concatenation multichance)

                const sameMultichoiceBet = existingMyMatchItemSelectionItems[sameMultichoiceBetIdIndex];

                if (sameMultichoiceBet) {
                  const { outcomeIds, outcomeId, oddId } = sameMultichoiceBet;

                  if (Array.isArray(outcomeIds)) {
                    sameMultichoiceBet.outcomeIds = [...new Set([...outcomeIds, actionPayloadOddId])];
                  } else {
                    sameMultichoiceBet.outcomeIds = [...new Set([outcomeId ?? oddId, actionPayloadOddId])];
                  }

                  delete sameMultichoiceBet.outcomeId;
                  delete sameMultichoiceBet.isFromMultichoice;

                  return existingMyMatchItemSelectionItems.map(
                    ({ betId, oddId, outcomeId, outcomeIds, isFromMultichoice }) => ({
                      betId,
                      outcomeId: outcomeId ?? oddId,
                      outcomeIds,
                      isFromMultichoice,
                    }),
                  );
                }
              }
            }

            const invertedIsFromMultichoice = tryToInvertIsFromMultichoiceProp();

            if (invertedIsFromMultichoice) {
              return invertedIsFromMultichoice;
            }

            return [
              ...existingMyMatchItemSelectionItems.map(
                ({ betId, oddId, outcomeId, outcomeIds, isFromMultichoice }) => ({
                  betId,
                  outcomeId: outcomeId ?? oddId,
                  outcomeIds,
                  isFromMultichoice,
                }),
              ),
              {
                betId: actionPayloadBetId,
                outcomeId: actionPayloadOddId,
                isFromMultichoice: actionPayloadIsFromMultichoice,
              },
            ];
          };

          const getOddsOutcomes = computeGetOddsOutcomes();

          if (Array.isArray(existingMyMatchItemSelectionItems)) {
            dispatch(
              getOdds(
                {
                  matchId: actionPayloadMatchId,
                  outcomes: getOddsOutcomes.map(({ betId, outcomeId, outcomeIds }) => ({
                    betId,
                    outcomeId,
                    outcomeIds,
                  })),
                },
                {
                  onSuccess: (result) => {
                    const { mymatchOdds: value, mymatchError } = result ?? {};
                    const { errorId } = mymatchError ?? {};

                    if (!errorId || !!~NoBasketSplitByDefaultMyMatchErrorIds.indexOf(errorId)) {
                      // La fusion est faisable

                      dispatch(
                        myMatchMerge({
                          itemIdsToRemove: [existingMyMatchItemOddId],
                          matchId: actionPayloadMatchId,
                          selectionItems: getOddsOutcomes.map((selectionItem) =>
                            getMyMatchSelectionItemFormat({
                              ...selectionItem,
                              matchId: actionPayloadMatchId,
                            }),
                          ),
                          value,
                          sourceElementUuid: actionPayloadSourceElementUuid,
                          oddId: actionPayloadOddId,
                        }),
                      );
                    } else {
                      // On ne peut pas fusionner la selection en un item MyMatch,
                      // on doit donc éclater la selection MyMatch existante en différents outcomes

                      dispatch(basketRequestSwitchMode({ mode: BasketModes.SIMPLE }));

                      dispatch(
                        myMatchSplit({
                          matchId: actionPayloadMatchId,
                          itemIdToRemove: existingMyMatchItemOddId,
                          splitedItems: [
                            ...existingMyMatchItemSelectionItems.map(
                              ({
                                oddId: selectionItemOddId,
                                outcomeId: selectionItemOutcomeId,
                                betId: selectionItemBetId,
                                matchId: selectionItemMatchId,
                                outcomeIds: selectionItemOutcomeIds,
                                isFromMultichoice: selectionItemIsFromMultichoice,
                              }) => ({
                                oddId: selectionItemOutcomeId ?? selectionItemOddId,
                                betId: selectionItemBetId,
                                matchId: selectionItemMatchId,
                                outcomeIds: selectionItemOutcomeIds,
                                isFromMultichoice: selectionItemIsFromMultichoice,
                              }),
                            ),
                            {
                              oddId: actionPayloadOddId,
                              betId: actionPayloadBetId,
                              matchId: actionPayloadMatchId,
                              isFromMultichoice: actionPayloadIsFromMultichoice,
                            },
                          ],
                          sourceElementUuid: actionPayloadSourceElementUuid,
                          oddId: actionPayloadOddId,
                        }),
                      );
                    }
                  },
                },
              ),
            );
          }
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_OUTCOME_DELETION) {
        // Appel à la method 'GetOdds' avec la future selection du futur item MyMatch
        // (toutes les selections de l’item MyMatch existant - l'item deselectionné)
        // et les hooks = { onSuccess, onError }

        const {
          myMatchBasketItemId: actionPayloadMyMatchBasketItemId,
          selectionItemOddId: actionPayloadSelectionItemOddId,
          itemFoundInOutcomeIds: actionPayloadItemFoundInOutcomeIds,
        } = actionPayload ?? {};

        const targetedMyMatchItem = basketItems[actionPayloadMyMatchBasketItemId];

        if (targetedMyMatchItem) {
          const {
            selectionItems: targetedMyMatchItemSelectionItems,
            oddId: targetedMyMatchItemOddId,
            matchId: targetedMyMatchMatchId,
          } = targetedMyMatchItem;

          if (Array.isArray(targetedMyMatchItemSelectionItems)) {
            const resultingSelectionItems = targetedMyMatchItemSelectionItems
              .reduce((acc, _selectionItem) => {
                if (actionPayloadItemFoundInOutcomeIds) {
                  const { outcomeIds: selectionItemOutcomeIds } = _selectionItem;

                  if (
                    Array.isArray(selectionItemOutcomeIds) &&
                    selectionItemOutcomeIds.find((_outcomeId) => _outcomeId === actionPayloadSelectionItemOddId)
                  ) {
                    const nextItem = { ..._selectionItem };

                    const nextOutcomeIds = selectionItemOutcomeIds.filter(
                      (_outcomeId) => _outcomeId !== actionPayloadSelectionItemOddId,
                    );

                    if (nextOutcomeIds.length === 1) {
                      nextItem.outcomeId = nextOutcomeIds[0];
                      nextItem.isFromMultichoice = true;
                      delete nextItem.outcomeIds;
                    } else {
                      nextItem.outcomeIds = nextOutcomeIds;
                    }

                    return [...acc, { ...nextItem }];
                  }
                } else {
                  const { oddId: selectionItemOddId, outcomeId: selectionItemOutcomeId } = _selectionItem;
                  const selectionItemOddsId = selectionItemOddId ?? selectionItemOutcomeId;

                  if (selectionItemOddsId && selectionItemOddsId === actionPayloadSelectionItemOddId) {
                    return acc;
                  }
                }

                return [...acc, _selectionItem];
              }, [])
              .filter((_selectionItem, i, reducedSelectionItems) => {
                const { outcomeId: selectionItemOutcomeId, isFromMultichoice: selectionItemIsFromMultichoice } =
                  _selectionItem;

                if (!selectionItemIsFromMultichoice) {
                  const nbOccurence = reducedSelectionItems.filter(
                    (k) => k.outcomeId === selectionItemOutcomeId,
                  ).length;

                  return nbOccurence === 1;
                }

                return true;
              });

            if (resultingSelectionItems.length) {
              dispatch(
                getOdds(
                  {
                    matchId: targetedMyMatchMatchId,
                    outcomes: getGetOddsOutcomesFromBasketItems(resultingSelectionItems),
                  },
                  {
                    onSuccess: (result) => {
                      const { mymatchOdds: value } = result ?? {};

                      dispatch(
                        myMatchMerge({
                          itemIdsToRemove: [targetedMyMatchItemOddId],
                          matchId: targetedMyMatchMatchId,
                          selectionItems: resultingSelectionItems,
                          value,
                        }),
                      );
                    },
                  },
                ),
              );
            }
          }
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_MERGE) {
        // Permet de regrouper plusieurs items classique en une selection MyMatch
        // Si jamais on detecte une selection contradictoire ou tout autre type d'erreur, le merge doit tout de même
        // se faire et l'on devra afficher les erreurs à l'utilisateur
        // Si un item MyMatch correspondant au matchId reçu existe déjà en cache il faut le charger à la place
        // pour éviter d'appeler l'API

        const { cachedMMAPIResponsesInfos } = basketState;
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const { onFinally } = actionHooks;

        const prevClassicItemsWithSameMatchId = basketItemsAsArray.filter(
          (item) => item && item.matchId === actionPayloadMatchId && !item.betbuilder,
        );

        if (prevClassicItemsWithSameMatchId.length > 1) {
          const nextSelectionItems = prevClassicItemsWithSameMatchId.reduce((acc, item) => {
            if (item.isFromMultichoice) {
              const outcomeIdsItemFound = acc.find(
                (_item) => Array.isArray(_item.outcomeIds) && _item.betId === item.betId,
              );
              const shouldBeOutcomeIds =
                prevClassicItemsWithSameMatchId.filter((_item) => _item.betId === item.betId && _item.isFromMultichoice)
                  .length > 1;

              if (outcomeIdsItemFound) {
                outcomeIdsItemFound.outcomeIds.push(item.outcomeId ?? item.oddId ?? item.oddsId);
                return acc;
              }

              if (shouldBeOutcomeIds) {
                const multichoicedItem = { ...item };
                multichoicedItem.outcomeIds = [
                  multichoicedItem.outcomeId ?? multichoicedItem.oddId ?? multichoicedItem.oddsId,
                ];
                delete multichoicedItem.outcomeId;
                delete multichoicedItem.isFromMultichoice;

                return [...acc, getMyMatchSelectionItemFormat({ ...multichoicedItem })];
              }
            }

            return [...acc, getMyMatchSelectionItemFormat(item)];
          }, []);

          const getOddsOutcomes = getGetOddsOutcomesFromBasketItems(nextSelectionItems);

          const onSuccess = (result) => {
            // La fusion est faisable
            const { mymatchOdds: value } = result ?? {};

            dispatch(
              myMatchMerge({
                itemIdsToRemove: prevClassicItemsWithSameMatchId.map((k) => k.oddId),
                matchId: actionPayloadMatchId,
                selectionItems: nextSelectionItems,
                value,
              }),
            );
          };

          const cachedMMAPIResponseInfos = cachedMMAPIResponsesInfos[actionPayloadMatchId];

          if (cachedMMAPIResponseInfos) {
            // Il y a du cache, on tente de l'utiliser

            const { outcomes, result } = cachedMMAPIResponseInfos;

            if (Array.isArray(outcomes) && _.isEqual(outcomes, getOddsOutcomes)) {
              onSuccess(result);

              if (typeof onFinally === 'function') {
                onFinally();
              }
            } else {
              // Le cache disponible ne correspond pas, on appelle l'API

              dispatch(
                getOdds(
                  {
                    matchId: actionPayloadMatchId,
                    outcomes: getOddsOutcomes,
                  },
                  {
                    onSuccess,
                    onFinally: () => {
                      if (typeof onFinally === 'function') {
                        onFinally();
                      }
                    },
                  },
                ),
              );
            }
          } else {
            if (typeof onFinally === 'function') {
              onFinally();
            }
          }
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_SPLIT) {
        // Permet de split une selection MyMatch en plusieurs items classiques

        const { oddId: actionPayloadOddId } = actionPayload ?? {};

        const myMatchItem = basketItems[actionPayloadOddId];
        const { selectionItems = [], matchId } = myMatchItem ?? {};

        dispatch(myMatchSplit({ matchId, itemIdToRemove: actionPayloadOddId, splitedItems: selectionItems }));

        const { onFinally } = actionHooks;

        if (typeof onFinally === 'function') {
          onFinally();
        }
      }

      if (actionType === CommonActionTypes.RECEIVE_ENTITIES) {
        // When receiving entities updates we should check if one of the cached mymatch could be impacted by those
        // changes. If we find occurence between the composition of one cached mymatch and one of the received (updated)
        // entities, we need to recall 'getOdds' to update the cached mymatch infos

        const {
          basket: { status: basketStatus },
        } = state;

        if (basketStatus !== BasketStatuses.VALIDATED && basketStatus !== BasketStatuses.PENDING) {
          const { bets, outcomes, odds, matches, myMatchResults: actionPayloadMyMatchResults } = actionPayload ?? {};

          if (actionPayloadMyMatchResults) {
            const { id, result } = actionPayloadMyMatchResults;

            if (!id) {
              // On est dans le cas d'un forward automatique de données liées à myMatch

              const { mymatchError } = result ?? {};
              const { errorId } = mymatchError ?? {};

              const match = history?.location?.pathname.match(
                new RegExp(RoutePatterns.MATCH.replace(':matchId', '(\\d+)(.*)$')),
              );

              if (match) {
                const [, matchIdasStr] = match;
                const matchId = parseInt(matchIdasStr, 10);

                if (errorId) {
                  dispatch(
                    basketAlterCachedMMAPIResponseInfos({
                      result: { mymatchError },
                      matchId,
                    }),
                  );
                }

                // On met à jour le panier
                dispatch(getOdds({ matchId }));
              }

              dispatch(receivedGetFutureOddsEntitiesUpdates(actionPayloadMyMatchResults));
              return next(action);
            }
          }

          const { cachedMMAPIResponsesInfos } = basketState;

          const cachedMatchIds: Array<number> = Object.keys(cachedMMAPIResponsesInfos).map((matchIdStr) =>
            parseInt(matchIdStr, 10),
          );
          const cacheAsArray = cachedMatchIds.map((matchIdStr) => cachedMMAPIResponsesInfos[matchIdStr]);
          const cachedBetIds: Array<number> = cacheAsArray.reduce(
            (acc, cacheItem) => [...acc, ...cacheItem.outcomes.map((k) => k.betId)],
            [],
          );
          const cachedOutcomeIds: Array<number> = cacheAsArray.reduce(
            (acc, cacheItem) => [...acc, ...cacheItem.outcomes.map((k) => k.outcomeId)],
            [],
          );

          const receivedOddIds: Array<number> = Object.keys(odds ?? {}).map((k) => parseInt(k, 10));
          const receivedOutcomeIds: Array<number> = Object.keys(outcomes ?? {}).map((k) => parseInt(k, 10));
          const receivedBetIds: Array<number> = Object.keys(bets ?? {}).map((k) => parseInt(k, 10));
          const receivedMatchIds: Array<number> = Object.keys(matches ?? {}).map((k) => parseInt(k, 10));

          const getGetOddsOnSuccess = (_matchId) => (result) => {
            const { mymatchOdds: value } = result ?? {};

            if (typeof value === 'number') {
              const {
                basket: { items: _basketItems },
              } = getState();
              const mmItemFound = Object.keys(_basketItems)
                .map((_itemId) => _basketItems[_itemId])
                .find((_item) => _item.betbuilder && _item.matchId === _matchId);

              if (mmItemFound) {
                const nextItem = { ...mmItemFound, value };

                dispatch(basketUpdateItemValue(nextItem));
              }
            }
          };

          if (receivedMatchIds.length && cachedMatchIds.length) {
            for (const receivedMatchId of receivedMatchIds) {
              for (const cachedMatchId of cachedMatchIds) {
                if (cachedMatchId === receivedMatchId) {
                  dispatch(
                    getOdds(
                      { matchId: cachedMatchId, outcomes: cachedMMAPIResponsesInfos[cachedMatchId].outcomes },
                      { onSuccess: getGetOddsOnSuccess(cachedMatchId) },
                    ),
                  );

                  const matchViewMode = selectMatchViewMode(state, { matchId: cachedMatchId });

                  if (matchViewMode === MatchViewModes.MY_MATCH) {
                    dispatch(getFutureOdds({ matchId: cachedMatchId, triggerLoading: false }));
                  }

                  return next(action);
                }
              }
            }
          }

          if (receivedBetIds.length && cachedBetIds.length) {
            for (const receivedBetId of receivedBetIds) {
              for (const cachedBetId of cachedBetIds) {
                if (cachedBetId === receivedBetId) {
                  const cacheItemTarget = cacheAsArray.find(
                    (item) => !!item.outcomes.find((k) => k.betId === cachedBetId),
                  );

                  if (cacheItemTarget) {
                    dispatch(
                      getOdds(
                        { matchId: cacheItemTarget.matchId, outcomes: cacheItemTarget.outcomes },
                        { onSuccess: getGetOddsOnSuccess(cacheItemTarget.matchId) },
                      ),
                    );

                    const matchViewMode = selectMatchViewMode(state, { matchId: cacheItemTarget.matchId });

                    if (matchViewMode === MatchViewModes.MY_MATCH) {
                      dispatch(getFutureOdds({ matchId: cacheItemTarget.matchId, triggerLoading: false }));
                    }

                    return next(action);
                  }
                }
              }
            }
          }

          if (receivedOutcomeIds.length && cachedOutcomeIds.length) {
            for (const receivedOutcomeId of receivedOutcomeIds) {
              for (const cachedOutcomeId of cachedOutcomeIds) {
                if (cachedOutcomeId === receivedOutcomeId) {
                  const cacheItemTarget = cacheAsArray.find(
                    (item) => !!item.outcomes.find((k) => k.outcomeId === cachedOutcomeId),
                  );

                  if (cacheItemTarget) {
                    dispatch(
                      getOdds(
                        { matchId: cacheItemTarget.matchId, outcomes: cacheItemTarget.outcomes },
                        { onSuccess: getGetOddsOnSuccess(cacheItemTarget.matchId) },
                      ),
                    );

                    const matchViewMode = selectMatchViewMode(state, { matchId: cacheItemTarget.matchId });

                    if (matchViewMode === MatchViewModes.MY_MATCH) {
                      dispatch(getFutureOdds({ matchId: cacheItemTarget.matchId, triggerLoading: false }));
                    }

                    return next(action);
                  }
                }
              }
            }
          }

          if (receivedOddIds.length && cachedOutcomeIds.length) {
            for (const receivedOddId of receivedOddIds) {
              for (const cachedOutcomeId of cachedOutcomeIds) {
                if (cachedOutcomeId === receivedOddId) {
                  const cacheItemTarget = cacheAsArray.find(
                    (item) => !!item.outcomes.find((k) => k.outcomeId === cachedOutcomeId),
                  );

                  if (cacheItemTarget) {
                    dispatch(
                      getOdds(
                        { matchId: cacheItemTarget.matchId, outcomes: cacheItemTarget.outcomes },
                        { onSuccess: getGetOddsOnSuccess(cacheItemTarget.matchId) },
                      ),
                    );

                    const matchViewMode = selectMatchViewMode(state, { matchId: cacheItemTarget.matchId });

                    if (matchViewMode === MatchViewModes.MY_MATCH) {
                      dispatch(getFutureOdds({ matchId: cacheItemTarget.matchId, triggerLoading: false }));
                    }

                    return next(action);
                  }
                }
              }
            }
          }
        }
      }

      if (actionType === MyMatchActionTypes.REQUEST_TOGGLE_MY_MATCH_MODE) {
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const { myMatchModeMatchIds } = myMatchState;

        if (actionPayloadMatchId) {
          const isMyMatchMode = ~myMatchModeMatchIds.indexOf(actionPayloadMatchId);

          if (isMyMatchMode) {
            dispatch(unsetMyMatchMode({ matchId: actionPayloadMatchId }));
          } else {
            dispatch(setMyMatchMode({ matchId: actionPayloadMatchId }));
          }
        }
      }

      if (actionType === MyMatchActionTypes.SET_MY_MATCH_MODE) {
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const nextedAction = next(action);

        dispatch(getFutureOdds({ matchId: actionPayloadMatchId }));

        return nextedAction;
      }

      if (actionType === MyMatchActionTypes.INJECT_TOP_MY_MATCH_INTO_BASKET) {
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const nextedAction = next(action);

        dispatch(getFutureOdds({ matchId: actionPayloadMatchId }));

        return nextedAction;
      }

      if (actionType === MyMatchActionTypes.REMOVE_MULTICHOICE_BET_SELECTION) {
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const nextedAction = next(action);

        dispatch(getFutureOdds({ matchId: actionPayloadMatchId }));

        return nextedAction;
      }

      if (actionType === MyMatchActionTypes.SPLIT) {
        const { matchId: actionPayloadMatchId } = actionPayload ?? {};
        const nextedAction = next(action);

        const newState = getState();
        const selectNbBasketMatchSelections = makeNbBasketMatchSelectionsSelector();
        const nbMatchBasketItems = selectNbBasketMatchSelections(newState, { matchId: actionPayloadMatchId });

        if (nbMatchBasketItems <= 1) {
          dispatch(basketRemoveCachedMMAPIResponseInfos({ matchId: actionPayloadMatchId }));
        }

        return nextedAction;
      }
    }

    return next(action);
  };

export default myMatchMiddleware;
